import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useDeleteAccount } from "@/lib/auth-me";
import { useAuthStore } from "@/lib/auth";

/**
 * Soft-delete the user. Reject if the caller is the sole OWNER of an
 * active workspace (operator must transfer ownership or delete the
 * workspace first). On success, all sessions are revoked and the
 * caller is bounced to the sign-in screen.
 */
export function DeleteAccountDialog({
  open,
  onClose,
  email,
}: {
  open: boolean;
  onClose: () => void;
  email: string;
}) {
  const del = useDeleteAccount();
  const clear = useAuthStore((s) => s.clear);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setPassword("");
    setConfirm("");
    setError(null);
  };

  const matches = confirm.trim().toLowerCase() === email.toLowerCase();

  const onSubmit = async () => {
    setError(null);
    if (!matches) {
      setError("The email you typed doesn't match.");
      return;
    }
    try {
      await del.mutateAsync({ password, confirmEmail: confirm.trim() });
      clear();
      const authUrl = import.meta.env.VITE_AUTH_APP_URL ?? "http://localhost:5174";
      window.location.assign(`${authUrl}/sign-in`);
    } catch (e) {
      setError(humanError((e as Error).message));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-destructive">Delete your account</DialogTitle>
          <DialogDescription>
            Soft-delete your account. You'll be signed out everywhere and
            won't be able to sign in. Contact support within 30 days to
            restore.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="delete-password">Password</Label>
            <Input
              id="delete-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="delete-confirm">
              Type <strong>{email}</strong> to confirm
            </Label>
            <Input
              id="delete-confirm"
              type="email"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder={email}
            />
          </div>
          {error && (
            <p className="text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded-md px-3 py-2">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onSubmit}
            disabled={!password || !matches || del.isPending}
          >
            {del.isPending ? "Deleting…" : "Delete my account"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function humanError(code: string): string {
  switch (code) {
    case "invalid_credentials":
      return "Password is incorrect.";
    case "email_mismatch":
      return "The email you typed doesn't match your account.";
    case "sole_owner_of_active_tenant":
      return "You're the only owner of this workspace. Transfer ownership or delete the workspace first.";
    default:
      return code || "Couldn't delete your account.";
  }
}
